import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { coursesAPI, applicationsAPI, profileAPI, statsAPI, favoritesAPI, userAPI, insightsAPI } from '@/lib/api';
import JourneyTracker from '@/components/JourneyTracker';
import JourneyDocumentManager from '@/components/JourneyDocumentManager';
import OfferResponsePanel from '@/components/OfferResponsePanel';
import StudentPortalEnhanced from '@/components/student/StudentPortalEnhanced';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import {
  GraduationCap,
  MapPin,
  Clock,
  FileText,
  Plus,
  Search,
  Edit,
  CheckCircle,
  XCircle,
  Loader2,
  BookOpen,
  Building,
  TrendingUp,
  Heart,
  HeartOff,
  User,
  Phone,
  Mail,
  Save,
  HelpCircle,
  Megaphone,
  X,
  Lock,
  Eye,
  EyeOff,
  Send,
  Play,
  Award,
  ChevronRight,
  Smartphone,
  Download,
  QrCode,
  RefreshCw
} from 'lucide-react';

const StudentDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, updateUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [profile, setProfile] = useState(null);
  const [applications, setApplications] = useState([]);
  const [recommendedCourses, setRecommendedCourses] = useState([]);
  const [recommendationMeta, setRecommendationMeta] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [stats, setStats] = useState({});
  const [activeTab, setActiveTab] = useState('applications');
  const [insights, setInsights] = useState([]);
  
  // Edit Profile Modal State
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editForm, setEditForm] = useState({
    full_name: '',
    phone: '',
    education_level: '',
    preferred_countries: [],
    budget_range: '',
    intake_date: '',
    field_of_study: '',
    english_test: '',
    english_score: '',
    work_experience_years: 0,
    additional_info: ''
  });

  // Change Password Modal State
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Student ID Card with QR Code
  const [showStudentCard, setShowStudentCard] = useState(false);
  const appUrl = typeof window !== 'undefined' ? window.location.origin : '';

  const countries = ['UK', 'USA', 'Canada', 'Ireland', 'France', 'Germany'];
  const educationLevels = [
    { value: 'high_school', label: 'High School / A-Levels' },
    { value: 'bachelors', label: "Bachelor's Degree" },
    { value: 'masters', label: "Master's Degree" },
    { value: 'phd', label: 'PhD / Doctorate' }
  ];
  const budgetRanges = [
    { value: 'low', label: 'Under £10,000/year' },
    { value: 'medium', label: '£10,000 - £20,000/year' },
    { value: 'high', label: '£20,000 - £35,000/year' },
    { value: 'premium', label: 'Over £35,000/year' }
  ];
  const englishTests = [
    { value: 'IELTS', label: 'IELTS' },
    { value: 'PEARSON_PTE', label: 'Pearson PTE' },
    { value: 'TOEFL', label: 'TOEFL' },
    { value: 'OXFORD', label: 'Oxford Test' },
    { value: 'DUOLINGO', label: 'Duolingo' },
    { value: 'NONE', label: 'Not taken yet' }
  ];
  const fieldsOfStudy = [
    'Business & Management',
    'Computer Science & IT',
    'Engineering',
    'Healthcare & Medicine',
    'Arts & Design',
    'Law',
    'Social Sciences',
    'Natural Sciences',
    'Education'
  ];

  // NOTE: `fetchData` MUST be declared via useCallback BEFORE the useEffect
  // that references it. With React 18 strict mode the function body is
  // evaluated before any callback runs, so a `useEffect(..., [fetchData])`
  // appearing above the `const fetchData = useCallback(...)` declaration
  // throws "Cannot access 'fetchData' before initialization" (TDZ) and
  // crashes the entire dashboard into a blank page.
  const fetchData = useCallback(async () => {
    try {
      const [profileRes, appsRes, recommendRes, statsRes, favoritesRes, insightsRes] = await Promise.all([
        profileAPI.get().catch(() => ({ data: null })),
        applicationsAPI.getAll().catch(() => ({ data: [] })),
        coursesAPI.getSmartRecommendations(12).catch(() => ({ data: { recommendations: [] } })),
        statsAPI.getDashboard().catch(() => ({ data: {} })),
        favoritesAPI.getAll().catch(() => ({ data: [] })),
        insightsAPI.getActive().catch(() => ({ data: [] }))
      ]);
      
      setProfile(profileRes.data);
      setApplications(appsRes.data || []);
      
      // Handle smart recommendations
      const recData = recommendRes.data;
      setRecommendedCourses(recData?.recommendations || []);
      setRecommendationMeta({
        profileComplete: recData?.profile_complete,
        profileCompleteness: recData?.profile_completeness,
        totalMatches: recData?.total_matches,
        filters: recData?.filters_applied
      });
      
      setStats(statsRes.data || {});
      setFavorites(favoritesRes.data || []);
      setFavoriteIds(new Set((favoritesRes.data || []).map(f => f.course?.id)));
      setInsights(insightsRes.data || []);
      
      // Initialize edit form with profile data
      if (profileRes.data) {
        setEditForm(prev => ({
          ...prev,
          full_name: user?.full_name || '',
          phone: user?.phone || '',
          education_level: profileRes.data.education_level || '',
          preferred_countries: profileRes.data.preferred_countries || [],
          budget_range: profileRes.data.budget_range || '',
          intake_date: profileRes.data.intake_date || '',
          field_of_study: profileRes.data.field_of_study || '',
          english_test: profileRes.data.english_test || '',
          english_score: profileRes.data.english_score || '',
          work_experience_years: profileRes.data.work_experience_years || 0,
          additional_info: profileRes.data.additional_info || ''
        }));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.full_name, user?.phone]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefreshAll = async () => {
    setRefreshing(true);
    try {
      await fetchData();
      toast.success('All data refreshed successfully!');
    } catch (error) {
      toast.error('Failed to refresh data');
    } finally {
      setRefreshing(false);
    }
  };

  const handleApply = async (courseId) => {
    try {
      await applicationsAPI.create({ course_id: courseId });
      toast.success('Application submitted successfully!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    }
  };

  const handleToggleFavorite = async (courseId, courseName) => {
    try {
      if (favoriteIds.has(courseId)) {
        await favoritesAPI.remove(courseId);
        setFavoriteIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(courseId);
          return newSet;
        });
        setFavorites(prev => prev.filter(f => f.course?.id !== courseId));
        toast.success(`Removed ${courseName} from wishlist`);
      } else {
        await favoritesAPI.add(courseId);
        setFavoriteIds(prev => new Set([...prev, courseId]));
        // Refresh favorites to get full course details
        const favRes = await favoritesAPI.getAll();
        setFavorites(favRes.data || []);
        toast.success(`Added ${courseName} to wishlist`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update wishlist');
    }
  };

  const handleSaveProfile = async () => {
    setEditLoading(true);
    try {
      // Update user info (name, phone)
      if (editForm.full_name !== user?.full_name || editForm.phone !== user?.phone) {
        await userAPI.update({
          full_name: editForm.full_name,
          phone: editForm.phone
        });
        // Update local user state
        if (updateUser) {
          updateUser({ ...user, full_name: editForm.full_name, phone: editForm.phone });
        }
      }
      
      // Update profile
      await profileAPI.update({
        education_level: editForm.education_level,
        preferred_countries: editForm.preferred_countries,
        budget_range: editForm.budget_range,
        intake_date: editForm.intake_date,
        field_of_study: editForm.field_of_study,
        english_test: editForm.english_test,
        english_score: editForm.english_score || null,
        work_experience_years: parseInt(editForm.work_experience_years) || 0,
        additional_info: editForm.additional_info || null
      });
      
      toast.success('Profile updated successfully!');
      setShowEditProfile(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setEditLoading(false);
    }
  };

  const handleChangePassword = async () => {
    // Validation
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      toast.error('Please fill in all password fields');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    setPasswordLoading(true);
    try {
      await userAPI.changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      toast.success('Password changed successfully!');
      setShowChangePassword(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleCountryToggle = (country) => {
    setEditForm(prev => {
      const countries = prev.preferred_countries || [];
      if (countries.includes(country)) {
        return { ...prev, preferred_countries: countries.filter(c => c !== country) };
      } else {
        return { ...prev, preferred_countries: [...countries, country] };
      }
    });
  };

  // Handle submit application to H E Direct (for B2C direct students)
  const handleSubmitToHEDirect = async (appId) => {
    try {
      await applicationsAPI.submitToHEDirect(appId);
      toast.success('Application submitted to H E Direct!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-600',
      submitted: 'bg-blue-100 text-blue-700',
      under_review: 'bg-amber-100 text-amber-700',
      accepted: 'bg-emerald-100 text-emerald-700',
      rejected: 'bg-red-100 text-red-700',
      enrolled: 'bg-purple-100 text-purple-700'
    };
    return colors[status] || colors.draft;
  };

  const formatCurrency = (amount, currency, country) => {
    const countryCurrencyMap = {
      'UK': 'GBP', 'United Kingdom': 'GBP',
      'USA': 'USD', 'United States': 'USD',
      'Canada': 'CAD', 'Australia': 'AUD',
      'Germany': 'EUR', 'France': 'EUR', 'Netherlands': 'EUR', 'Ireland': 'EUR',
      'New Zealand': 'NZD'
    };
    const currencyCode = currency || countryCurrencyMap[country] || 'GBP';
    const locale = currencyCode === 'GBP' ? 'en-GB' : currencyCode === 'EUR' ? 'de-DE' : 'en-US';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fa] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fa] dark:bg-slate-900 transition-colors">
      <Navbar />
      
      <main className="pt-20 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Welcome Section - Bordered */}
          <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-2xl p-6 mb-6 transition-colors">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center">
                  <GraduationCap className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-semibold text-gray-800 dark:text-white">
                    Welcome back, {user?.full_name?.split(' ')[0]}!
                  </h1>
                  <p className="text-gray-500 dark:text-gray-400 text-sm sm:text-base">Track your applications and explore new courses</p>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2 sm:gap-3">
                {/* Refresh All Data Button */}
                <Button
                  onClick={handleRefreshAll}
                  disabled={refreshing}
                  variant="outline"
                  size="sm"
                  className="border-green-300 dark:border-green-700 text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30 h-9 sm:h-10"
                  data-testid="refresh-all-btn"
                >
                  <RefreshCw className={`w-4 h-4 sm:mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                  <span className="hidden sm:inline">{refreshing ? 'Refreshing...' : 'Refresh All'}</span>
                </Button>
                {/* Student ID Card / QR Code Button */}
                <Button
                  onClick={() => setShowStudentCard(true)}
                  variant="outline"
                  size="sm"
                  className="border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30 h-9 sm:h-10"
                  data-testid="student-card-btn"
                >
                  <QrCode className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">My ID Card</span>
                </Button>
                {profile && (
                  <Button
                    onClick={() => setShowEditProfile(true)}
                    variant="outline"
                    size="sm"
                    className="border-gray-300 dark:border-slate-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 h-9 sm:h-10"
                    data-testid="edit-profile-btn"
                  >
                    <Edit className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Edit Profile</span>
                  </Button>
                )}
                <Button
                  onClick={() => window.location.href = '/training'}
                  variant="outline"
                  size="sm"
                  className="border-teal-300 dark:border-teal-700 text-teal-700 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/30 h-9 sm:h-10"
                  data-testid="training-btn"
                >
                  <Play className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Training</span>
                </Button>
                <Button
                  onClick={() => setShowChangePassword(true)}
                  variant="outline"
                  size="sm"
                  className="border-gray-300 dark:border-slate-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 h-9 sm:h-10"
                  data-testid="change-password-btn"
                >
                  <Lock className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Change Password</span>
                </Button>
                {!profile ? (
                  <Button
                    onClick={() => navigate('/profile-builder')}
                    size="sm"
                    className="bg-teal-600 text-white hover:bg-teal-700 font-medium h-9 sm:h-10"
                    data-testid="complete-profile-btn"
                  >
                    <Edit className="w-4 h-4 mr-1 sm:mr-2" />
                    <span className="text-sm">Complete Profile</span>
                  </Button>
                ) : (
                  <Link to="/courses">
                    <Button size="sm" className="bg-teal-600 text-white hover:bg-teal-700 font-medium h-9 sm:h-10" data-testid="search-courses-btn">
                      <Search className="w-4 h-4 mr-1 sm:mr-2" />
                      <span className="text-sm">Search Courses</span>
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </div>

          {/* Profile Incomplete Alert */}
          {!profile && (
            <div className="bg-white dark:bg-slate-800 rounded-xl p-4 sm:p-6 mb-6 border-2 border-amber-200 dark:border-amber-800 transition-colors">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center flex-shrink-0">
                  <Edit className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-1">Complete Your Profile</h3>
                  <p className="text-gray-500 dark:text-gray-400 mb-4">
                    To get personalized course recommendations and apply for programs, please complete your profile first.
                  </p>
                  <Button
                    onClick={() => navigate('/profile-builder')}
                    className="bg-teal-600 text-white hover:bg-teal-700 font-medium"
                  >
                    Build Profile Now
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Stats Cards - Bordered Section */}
          <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-2xl p-6 mb-6 transition-colors">
            <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-200 dark:border-slate-600">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-800 dark:text-white">Your Progress</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Quick overview of your journey</p>
              </div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-gray-50 dark:bg-slate-700 rounded-xl p-5 border border-gray-200 dark:border-slate-600 hover:border-blue-400 transition-all">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold text-gray-800 dark:text-white">{applications.length}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Applications</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 dark:bg-slate-700 rounded-xl p-5 border border-gray-200 dark:border-slate-600 hover:border-emerald-400 transition-all">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold text-gray-800 dark:text-white">{applications.filter(a => a.status === 'accepted').length}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Accepted</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 dark:bg-slate-700 rounded-xl p-5 border border-gray-200 dark:border-slate-600 hover:border-pink-400 transition-all">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-pink-100 dark:bg-pink-900/50 flex items-center justify-center">
                    <Heart className="w-5 h-5 text-pink-600 dark:text-pink-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold text-gray-800 dark:text-white">{favorites.length}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Wishlist</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 dark:bg-slate-700 rounded-xl p-5 border border-gray-200 dark:border-slate-600 hover:border-purple-400 transition-all">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold text-gray-800 dark:text-white">{profile?.preferred_countries?.length || 0}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Target Countries</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Scholarships Banner */}
          <div 
            onClick={() => navigate('/scholarships')}
            className="bg-white dark:bg-slate-800 rounded-xl p-5 mb-6 border-2 border-amber-200 dark:border-amber-800 hover:border-amber-300 dark:hover:border-amber-700 cursor-pointer transition-all group"
            data-testid="scholarships-banner"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center">
                  <Award className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white group-hover:text-teal-700 dark:group-hover:text-teal-400 transition-colors">
                    Explore Scholarships
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Chevening, GREAT, Commonwealth, Rhodes & more - Find funding for your studies
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-teal-600 dark:text-teal-400">
                <span className="hidden sm:inline text-sm font-medium">Browse All</span>
                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </div>

          {/* Tabs for Applications and Wishlist - Bordered Section */}
          <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-2xl p-6 mb-6 transition-colors">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <div className="border-b border-gray-200 dark:border-slate-600 pb-4 mb-4">
                <TabsList className="bg-gray-100 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 p-1.5 rounded-xl">
                  <TabsTrigger 
                    value="applications" 
                    className="data-[state=active]:bg-teal-600 data-[state=active]:text-white rounded-lg text-gray-600 dark:text-gray-300"
                    data-testid="tab-applications"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    My Applications ({applications.length})
                  </TabsTrigger>
                  <TabsTrigger 
                    value="wishlist" 
                    className="data-[state=active]:bg-teal-600 data-[state=active]:text-white rounded-lg text-gray-600 dark:text-gray-300"
                    data-testid="tab-wishlist"
                  >
                    <Heart className="w-4 h-4 mr-2" />
                    Wishlist ({favorites.length})
                  </TabsTrigger>
                  <TabsTrigger 
                    value="portal" 
                    className="data-[state=active]:bg-teal-600 data-[state=active]:text-white rounded-lg text-gray-600 dark:text-gray-300"
                    data-testid="tab-portal"
                  >
                    <User className="w-4 h-4 mr-2" />
                    My Portal
                  </TabsTrigger>
                </TabsList>
              </div>

            {/* Applications Tab */}
            <TabsContent value="applications" className="mt-6">
              <div className="grid lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2">
                  {applications.length === 0 ? (
                    <div className="bg-white rounded-xl p-8 text-center border border-gray-200">
                      <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-slate-700 flex items-center justify-center mx-auto mb-4">
                        <FileText className="w-8 h-8 text-gray-500 dark:text-gray-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-800 mb-2">No Applications Yet</h3>
                      <p className="text-gray-500 mb-6">Start exploring courses and submit your first application</p>
                      <Link to="/courses">
                        <Button className="bg-teal-600 text-white hover:bg-teal-700 font-medium">
                          Browse Courses
                        </Button>
                      </Link>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {applications.map((app) => (
                        <div 
                          key={app.id} 
                          className="bg-white dark:bg-slate-800 rounded-xl p-5 border-2 border-gray-200 dark:border-slate-700 transition-colors hover:border-teal-300 cursor-pointer" 
                          data-testid={`application-${app.id}`}
                          onClick={() => window.location.href = `/application/${app.id}`}
                        >
                          {/* Application Card - Simplified View */}
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0 overflow-hidden">
                              <div className="flex flex-wrap items-center gap-2 mb-2">
                                <Badge className="bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 text-xs">
                                  {(app.journey_stage || 'inquiry').replace(/_/g, ' ')}
                                </Badge>
                                <Badge className={getStatusColor(app.status)}>
                                  {app.status.replace('_', ' ')}
                                </Badge>
                                {app.agent_name ? (
                                  <Badge variant="outline" className="border-emerald-300 text-emerald-700 dark:text-emerald-400 text-xs">
                                    Via Agent: {app.agent_name}
                                  </Badge>
                                ) : app.submitted_to_hedirect ? (
                                  <Badge className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs">
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Submitted
                                  </Badge>
                                ) : (
                                  <Badge className="bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs">
                                    Draft
                                  </Badge>
                                )}
                              </div>
                              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-1 truncate" title={app.course_name}>{app.course_name}</h3>
                              <p className="text-gray-500 dark:text-gray-400 flex items-center gap-2 truncate">
                                <Building className="w-4 h-4 flex-shrink-0" />
                                <span className="truncate">{app.university_name}</span>
                              </p>
                              <p className="text-gray-500 dark:text-gray-400 flex items-center gap-2 mt-1">
                                <MapPin className="w-4 h-4 flex-shrink-0" />
                                <span className="truncate">{app.country}</span>
                              </p>
                            </div>
                            <div className="flex flex-col items-end gap-2 flex-shrink-0">
                              {!app.agent_name && !app.submitted_to_hedirect && (
                                <Button
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSubmitToHEDirect(app.id);
                                  }}
                                  className="bg-teal-600 text-white hover:bg-teal-700 font-medium"
                                >
                                  <Send className="w-4 h-4 mr-1" />
                                  Submit
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.location.href = `/application/${app.id}`;
                                }}
                                className="border-gray-300 dark:border-slate-600 text-gray-600 dark:text-gray-300"
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                View Journey
                              </Button>
                              <div className="text-right">
                                <p className="text-xs text-gray-500 dark:text-gray-400">Applied</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {new Date(app.created_at).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Profile Summary */}
                <div className="space-y-6">
                  {profile && (
                    <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border-2 border-gray-200 dark:border-slate-700 transition-colors">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">Your Profile</h3>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-500 dark:text-gray-400">Education</span>
                          <span className="text-gray-800 capitalize">{profile.education_level?.replace('_', ' ')}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500 dark:text-gray-400">Field</span>
                          <span className="text-gray-800 dark:text-white">{profile.field_of_study}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500 dark:text-gray-400">English Test</span>
                          <span className="text-gray-800 dark:text-white">{profile.english_test}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500 dark:text-gray-400">Target Intake</span>
                          <span className="text-gray-800 dark:text-white">{profile.intake_date}</span>
                        </div>
                      </div>
                      <Button
                        onClick={() => setShowEditProfile(true)}
                        variant="outline"
                        className="w-full mt-4 border-gray-300 text-gray-600 hover:bg-gray-100"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Edit Profile
                      </Button>
                    </div>
                  )}

                  {/* Insights/Announcements */}
                  {insights.length > 0 && (
                    <div className="bg-white border border-gray-200 rounded-xl rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <Megaphone className="w-5 h-5 text-teal-600" />
                        Latest Updates
                      </h3>
                      <div className="space-y-3">
                        {insights.slice(0, 3).map((insight) => (
                          <div 
                            key={insight.id} 
                            className={`p-3 rounded-lg ${
                              insight.priority === 'urgent' 
                                ? 'bg-red-500/10 border border-red-500/30' 
                                : insight.priority === 'important' 
                                  ? 'bg-teal-500/10 border border-teal-300' 
                                  : 'bg-[#e9ecef]/50'
                            }`}
                            data-testid={`insight-${insight.id}`}
                          >
                            <div className="flex items-start gap-2">
                              {insight.priority === 'urgent' && (
                                <Badge className="bg-red-100 text-red-600 text-xs shrink-0">Urgent</Badge>
                              )}
                              {insight.priority === 'important' && (
                                <Badge className="bg-teal-100 text-teal-600 text-xs shrink-0">Important</Badge>
                              )}
                            </div>
                            <p className={`text-sm font-medium mt-1 ${
                              insight.priority === 'urgent' ? 'text-red-700' : 
                              insight.priority === 'important' ? 'text-amber-700' : 'text-gray-800'
                            }`}>{insight.title}</p>
                            <p className="text-gray-500 text-xs mt-1 line-clamp-2">{insight.content}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Quick Actions */}
                  <div className="bg-white border border-gray-200 rounded-xl rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Quick Actions</h3>
                    <div className="space-y-3">
                      <Link to="/courses" className="block">
                        <Button variant="outline" className="w-full justify-start border-gray-300 text-gray-600 hover:bg-gray-100">
                          <Search className="w-4 h-4 mr-2" />
                          Search All Courses
                        </Button>
                      </Link>
                      <Link to="/destinations" className="block">
                        <Button variant="outline" className="w-full justify-start border-gray-300 text-gray-600 hover:bg-gray-100">
                          <MapPin className="w-4 h-4 mr-2" />
                          Explore Destinations
                        </Button>
                      </Link>
                      <Link to="/check-eligibility" className="block">
                        <Button variant="outline" className="w-full justify-start border-gray-300 text-gray-600 hover:bg-gray-100">
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Check Eligibility
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Wishlist Tab */}
            <TabsContent value="wishlist" className="mt-6">
              {favorites.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-xl rounded-xl p-8 text-center">
                  <div className="w-16 h-16 rounded-full bg-[#e9ecef] flex items-center justify-center mx-auto mb-4">
                    <Heart className="w-8 h-8 text-gray-500 dark:text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Your Wishlist is Empty</h3>
                  <p className="text-gray-500 mb-6">Save courses you&apos;re interested in to compare them later</p>
                  <Link to="/courses">
                    <Button className="bg-teal-600 text-white hover:bg-teal-700 font-semibold">
                      Browse Courses
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {favorites.map((fav) => (
                    <div key={fav.favorite_id} className="bg-white border border-gray-200 rounded-xl rounded-xl p-5 hover:border-teal-300 transition-colors" data-testid={`wishlist-${fav.course?.id}`}>
                      <div className="flex items-start justify-between mb-3">
                        <Badge variant="outline" className="border-teal-400 text-teal-600">
                          {fav.course?.degree_level}
                        </Badge>
                        <button
                          onClick={() => handleToggleFavorite(fav.course?.id, fav.course?.name)}
                          className="text-pink-600 hover:text-pink-700 transition-colors"
                          data-testid={`remove-favorite-${fav.course?.id}`}
                        >
                          <Heart className="w-5 h-5 fill-current" />
                        </button>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-800 mb-2">{fav.course?.name}</h3>
                      <p className="text-gray-500 text-sm mb-2">{fav.course?.university_name}</p>
                      <p className="text-gray-500 text-sm flex items-center gap-1 mb-4">
                        <MapPin className="w-3 h-3" />
                        {fav.course?.country}
                      </p>
                      
                      <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {fav.course?.duration_months || fav.course?.duration} months
                        </span>
                        <span className="text-teal-600 font-medium">
                          {formatCurrency(fav.course?.tuition_fee, fav.course?.currency, fav.course?.country)}
                        </span>
                      </div>
                      
                      <Button
                        onClick={() => handleApply(fav.course?.id)}
                        className="w-full bg-teal-600/10 text-teal-600 hover:bg-teal-600 hover:text-white font-medium"
                        data-testid={`apply-wishlist-${fav.course?.id}`}
                      >
                        Apply Now
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Portal Tab - Documents, Messages, Payments */}
            <TabsContent value="portal" className="mt-6">
              <StudentPortalEnhanced user={user} />
            </TabsContent>
            </Tabs>
          </div>

          {/* Recommended Courses - Bordered Section */}
          <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-2xl p-6 mb-6 transition-colors">
            <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-200 dark:border-slate-600">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
                  {recommendationMeta?.profileComplete ? 'Recommended For You' : 'Explore Courses'}
                </h2>
                {recommendationMeta?.totalMatches > 0 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {recommendationMeta.totalMatches} courses match your profile
                    {recommendationMeta?.profileCompleteness < 100 && (
                      <span className="text-teal-600 dark:text-teal-400 ml-2">
                        • Profile {recommendationMeta.profileCompleteness}% complete
                      </span>
                    )}
                  </p>
                )}
              </div>
              <Link to="/courses">
                <Button variant="ghost" className="text-teal-600 hover:text-teal-700">
                  View All Courses
                </Button>
              </Link>
            </div>
            
            {/* Filters Applied Banner */}
            {recommendationMeta?.filters && recommendationMeta.profileComplete && (
              <div className="mb-6 p-3 bg-teal-600/5 rounded-lg border border-teal-500/20">
                <p className="text-xs text-gray-500 mb-2">Matching based on:</p>
                <div className="flex flex-wrap gap-2">
                  {recommendationMeta.filters.countries?.length > 0 && (
                    <Badge variant="outline" className="text-xs border-gray-300 text-gray-600 dark:text-gray-300">
                      <MapPin className="w-3 h-3 mr-1" />
                      {recommendationMeta.filters.countries.join(', ')}
                    </Badge>
                  )}
                  {recommendationMeta.filters.field && (
                    <Badge variant="outline" className="text-xs border-gray-300 text-gray-600 dark:text-gray-300">
                      <BookOpen className="w-3 h-3 mr-1" />
                      {recommendationMeta.filters.field}
                    </Badge>
                  )}
                  {recommendationMeta.filters.max_budget && (
                    <Badge variant="outline" className="text-xs border-gray-300 text-gray-600 dark:text-gray-300">
                      Up to £{recommendationMeta.filters.max_budget.toLocaleString()}
                    </Badge>
                  )}
                  {recommendationMeta.filters.english_score > 0 && (
                    <Badge variant="outline" className="text-xs border-gray-300 text-gray-600 dark:text-gray-300">
                      IELTS {recommendationMeta.filters.english_score}
                    </Badge>
                  )}
                </div>
              </div>
            )}
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {recommendedCourses.slice(0, 6).map((course) => (
                <div key={course.id} className="bg-white border border-gray-200 rounded-xl rounded-xl p-5 hover:border-teal-300 transition-colors relative" data-testid={`course-card-${course.id}`}>
                  {/* Match Score Badge */}
                  {course.match_score && (
                    <div className="absolute -top-2 -right-2 bg-teal-600 text-slate-900 text-xs font-semibold px-2 py-1 rounded-full">
                      {course.match_score}% match
                    </div>
                  )}
                  
                  <div className="flex items-start justify-between mb-3">
                    <Badge variant="outline" className="border-teal-400 text-teal-600">
                      {course.degree_level}
                    </Badge>
                    <button
                      onClick={() => handleToggleFavorite(course.id, course.name)}
                      className={`transition-colors ${favoriteIds.has(course.id) ? 'text-pink-600' : 'text-gray-500 hover:text-pink-600'}`}
                      data-testid={`favorite-${course.id}`}
                    >
                      <Heart className={`w-5 h-5 ${favoriteIds.has(course.id) ? 'fill-current' : ''}`} />
                    </button>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">{course.name}</h3>
                  <p className="text-gray-500 text-sm mb-2">{course.university_name}</p>
                  
                  {/* Match Reasons */}
                  {course.match_reasons && course.match_reasons.length > 0 && (
                    <div className="mb-3 space-y-1">
                      {course.match_reasons.slice(0, 2).map((reason, idx) => (
                        <p key={idx} className="text-xs text-green-600 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          {reason}
                        </p>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {course.duration_months || course.duration} months
                    </span>
                    <span className="text-teal-600 font-medium">
                      {formatCurrency(course.tuition_fee, course.currency, course.country)}
                    </span>
                  </div>
                  
                  <Button
                    onClick={() => handleApply(course.id)}
                    className="w-full bg-teal-600/10 text-teal-600 hover:bg-teal-600 hover:text-white font-medium"
                    data-testid={`apply-${course.id}`}
                  >
                    Apply Now
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />

      {/* Edit Profile Dialog */}
      <Dialog open={showEditProfile} onOpenChange={setShowEditProfile}>
        <DialogContent className="bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-800 dark:text-white flex items-center gap-2">
              <User className="w-5 h-5 text-teal-600" />
              Edit Your Profile
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Personal Info */}
            <div className="space-y-4">
              <h3 className="text-gray-800 dark:text-white font-medium border-b border-gray-200 dark:border-slate-600 pb-2">Personal Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-600 dark:text-gray-300">Full Name</Label>
                  <Input
                    value={editForm.full_name}
                    onChange={(e) => setEditForm({...editForm, full_name: e.target.value})}
                    className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-800 dark:text-white mt-1"
                    data-testid="edit-name"
                  />
                </div>
                <div>
                  <Label className="text-gray-600 dark:text-gray-300">Phone</Label>
                  <Input
                    value={editForm.phone}
                    onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                    className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-800 dark:text-white mt-1"
                    data-testid="edit-phone"
                  />
                </div>
              </div>
            </div>

            {/* Education */}
            <div className="space-y-4">
              <h3 className="text-gray-800 dark:text-white font-medium border-b border-gray-200 dark:border-slate-600 pb-2">Education Background</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-600 dark:text-gray-300">Education Level</Label>
                  <Select value={editForm.education_level} onValueChange={(v) => setEditForm({...editForm, education_level: v})}>
                    <SelectTrigger className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-800 dark:text-white mt-1" data-testid="edit-education">
                      <SelectValue placeholder="Select level" />
                    </SelectTrigger>
                    <SelectContent className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600">
                      {educationLevels.map(level => (
                        <SelectItem key={level.value} value={level.value} className="text-gray-800 dark:text-white">{level.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-600 dark:text-gray-300">Field of Study</Label>
                  <Select value={editForm.field_of_study} onValueChange={(v) => setEditForm({...editForm, field_of_study: v})}>
                    <SelectTrigger className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-800 dark:text-white mt-1" data-testid="edit-field">
                      <SelectValue placeholder="Select field" />
                    </SelectTrigger>
                    <SelectContent className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600">
                      {fieldsOfStudy.map(field => (
                        <SelectItem key={field} value={field} className="text-gray-800 dark:text-white">{field}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* English Test */}
            <div className="space-y-4">
              <h3 className="text-gray-800 dark:text-white font-medium border-b border-gray-200 dark:border-slate-600 pb-2">English Proficiency</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-600 dark:text-gray-300">English Test</Label>
                  <Select value={editForm.english_test} onValueChange={(v) => setEditForm({...editForm, english_test: v})}>
                    <SelectTrigger className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-800 dark:text-white mt-1" data-testid="edit-english-test">
                      <SelectValue placeholder="Select test" />
                    </SelectTrigger>
                    <SelectContent className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600">
                      {englishTests.map(test => (
                        <SelectItem key={test.value} value={test.value} className="text-gray-800 dark:text-white">{test.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {editForm.english_test && editForm.english_test !== 'NONE' && (
                  <div>
                    <Label className="text-gray-600 dark:text-gray-300">Score</Label>
                    <Input
                      value={editForm.english_score}
                      onChange={(e) => setEditForm({...editForm, english_score: e.target.value})}
                      placeholder="e.g., 7.0"
                      className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-800 dark:text-white mt-1"
                      data-testid="edit-english-score"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Preferences */}
            <div className="space-y-4">
              <h3 className="text-gray-800 dark:text-white font-medium border-b border-gray-200 dark:border-slate-600 pb-2">Study Preferences</h3>
              
              <div>
                <Label className="text-gray-600 dark:text-gray-300 mb-2 block">Preferred Countries</Label>
                <div className="flex flex-wrap gap-2">
                  {countries.map(country => (
                    <button
                      key={country}
                      type="button"
                      onClick={() => handleCountryToggle(country)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                        editForm.preferred_countries?.includes(country)
                          ? 'bg-teal-600 text-white'
                          : 'bg-gray-100 dark:bg-slate-600 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-500'
                      }`}
                      data-testid={`country-${country}`}
                    >
                      {country}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-600 dark:text-gray-300">Budget Range</Label>
                  <Select value={editForm.budget_range} onValueChange={(v) => setEditForm({...editForm, budget_range: v})}>
                    <SelectTrigger className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-800 dark:text-white mt-1" data-testid="edit-budget">
                      <SelectValue placeholder="Select budget" />
                    </SelectTrigger>
                    <SelectContent className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600">
                      {budgetRanges.map(range => (
                        <SelectItem key={range.value} value={range.value} className="text-gray-800 dark:text-white">{range.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-600 dark:text-gray-300">Target Intake</Label>
                  <Input
                    value={editForm.intake_date}
                    onChange={(e) => setEditForm({...editForm, intake_date: e.target.value})}
                    placeholder="e.g., September 2025"
                    className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-800 dark:text-white mt-1"
                    data-testid="edit-intake"
                  />
                </div>
              </div>

              <div>
                <Label className="text-gray-600 dark:text-gray-300">Work Experience (Years)</Label>
                <Input
                  type="number"
                  min="0"
                  max="30"
                  value={editForm.work_experience_years}
                  onChange={(e) => setEditForm({...editForm, work_experience_years: e.target.value})}
                  className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-800 dark:text-white mt-1"
                  data-testid="edit-experience"
                />
              </div>

              <div>
                <Label className="text-gray-600 dark:text-gray-300">Additional Information</Label>
                <Textarea
                  value={editForm.additional_info}
                  onChange={(e) => setEditForm({...editForm, additional_info: e.target.value})}
                  placeholder="Any additional details you'd like to share..."
                  className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-800 dark:text-white mt-1"
                  rows={3}
                  data-testid="edit-additional"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                onClick={() => setShowEditProfile(false)}
                variant="outline"
                className="flex-1 border-gray-300 dark:border-slate-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveProfile}
                disabled={editLoading}
                className="flex-1 bg-teal-600 text-white hover:bg-teal-700 font-semibold"
                data-testid="save-profile-btn"
              >
                {editLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={showChangePassword} onOpenChange={setShowChangePassword}>
        <DialogContent className="bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-800 dark:text-white flex items-center gap-2">
              <Lock className="w-5 h-5 text-teal-600" />
              Change Password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-gray-600 dark:text-gray-300">Current Password</Label>
              <Input
                type="password"
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                className="bg-[#e9ecef] border-gray-200"
                placeholder="Enter current password"
                data-testid="current-password"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-600 dark:text-gray-300">New Password</Label>
              <Input
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                className="bg-[#e9ecef] border-gray-200"
                placeholder="Enter new password (min 6 characters)"
                data-testid="new-password"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-600 dark:text-gray-300">Confirm New Password</Label>
              <Input
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                className="bg-[#e9ecef] border-gray-200"
                placeholder="Confirm new password"
                data-testid="confirm-password"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowChangePassword(false);
                  setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                }}
                className="flex-1 border-gray-300 text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </Button>
              <Button
                onClick={handleChangePassword}
                disabled={passwordLoading}
                className="flex-1 bg-teal-600 text-white hover:bg-teal-700 font-semibold"
                data-testid="save-password-btn"
              >
                {passwordLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4 mr-2" />
                    Update Password
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Student ID Card Modal with QR Code */}
      <Dialog open={showStudentCard} onOpenChange={setShowStudentCard}>
        <DialogContent className="max-w-md bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-gray-800 dark:text-white flex items-center gap-2">
              <QrCode className="w-5 h-5 text-teal-600" />
              Student ID Card
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Student Card Preview */}
            <div className="bg-gradient-to-br from-teal-600 to-teal-800 rounded-2xl p-6 text-white relative overflow-hidden">
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 right-0 w-40 h-40 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-white rounded-full translate-y-1/2 -translate-x-1/2" />
              </div>
              
              <div className="relative z-10">
                {/* Header */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <GraduationCap className="w-7 h-7" />
                  </div>
                  <div>
                    <p className="text-teal-200 text-xs font-medium">H E DIRECT CONSULTANTS</p>
                    <p className="text-white text-lg font-bold">Student ID</p>
                  </div>
                </div>
                
                {/* Student Info */}
                <div className="mb-4">
                  <p className="text-2xl font-bold mb-1">{user?.full_name}</p>
                  <p className="text-teal-200 text-sm">{user?.email}</p>
                </div>
                
                {/* Student ID */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-teal-200 text-xs">STUDENT ID</p>
                    <p className="font-mono text-sm">{user?.id?.slice(0, 8).toUpperCase()}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-teal-200 text-xs">MEMBER SINCE</p>
                    <p className="text-sm">{new Date(user?.created_at).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* QR Code Section */}
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center gap-2 text-gray-600 dark:text-gray-300">
                <Smartphone className="w-5 h-5 text-teal-600" />
                <p className="text-sm font-medium">Scan to Install App</p>
              </div>
              
              <div className="inline-block p-3 sm:p-4 bg-white rounded-xl border-2 border-gray-200 dark:border-slate-600">
                <QRCodeSVG 
                  value={appUrl} 
                  size={140}
                  className="w-[120px] h-[120px] sm:w-[160px] sm:h-[160px]"
                  level="H"
                  includeMargin={false}
                  bgColor="white"
                  fgColor="#0d9488"
                />
              </div>
              
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Scan this QR code with your phone camera to install the H E Direct app on your device
              </p>
            </div>

            {/* Install Instructions */}
            <div className="bg-teal-50 dark:bg-teal-900/30 rounded-xl p-4 border border-teal-200 dark:border-teal-800">
              <p className="text-sm font-medium text-teal-800 dark:text-teal-300 mb-2 flex items-center gap-2">
                <Download className="w-4 h-4" />
                How to Install
              </p>
              <ol className="text-xs text-teal-700 dark:text-teal-400 space-y-1 list-decimal list-inside">
                <li>Scan the QR code with your phone</li>
                <li>Open the link in your browser</li>
                <li>Tap &quot;Add to Home Screen&quot; (iOS) or &quot;Install&quot; (Android)</li>
                <li>Access H E Direct like a native app!</li>
              </ol>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudentDashboard;
